import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { ChangeEvent } from "utils/history/Event";

import {
	createResource,
	getResourceContent,
	getResourceInfo,
	updateResourceContent
} from "resource/ResourceService";

import {
	InformationRight,
	ReportFieldRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { ImageField as UploadField } from "../FieldService";

export type UploadInputProps = {
	field: UploadField;
	rights: ReportFieldRights;
	editable: boolean;

	onChange: (event: ChangeEvent, valid: boolean) => void;
};

const MAX_SIZE_MB = 10;

type FileNames = {
	[key: number]: string;
};

export default function UploadInput(
	props: Readonly<UploadInputProps>
): JSX.Element {
	const { t } = useTranslation();

	const [files, setFiles] = useState<FileNames>({});
	const [error, setError] = useState<string>();

	// We only load the files information on the initial render.
	useEffect(() => {
		Promise.all(
			props.field.value.map(id =>
				getResourceInfo(id).then(resource => ({ [id]: resource.name }))
			)
		).then((entries: FileNames[]) => {
			setFiles(entries.reduce((a, b) => ({ ...a, ...b }), {}));
		});
	}, []);

	async function uploadFile(file: File): Promise<void> {
		setError(undefined);
		if (file.size > MAX_SIZE_MB * 1024 * 1024) {
			setError(t("fileMaxSize", { size: `${MAX_SIZE_MB}MiB` }));
			return;
		}

		const data = new FormData();
		data.append("file", file);
		createResource({
			name: file.name,
			mediaType: file.type,
			userRights: []
		})
			.then(location => {
				const id = location.substring(location.lastIndexOf("/") + 1);
				return Number.parseInt(id, 10);
			})
			.then(id =>
				updateResourceContent(id, data).then(() => {
					setFiles(previous => ({ ...previous, [id]: file.name }));

					const newValue = props.field.value.concat(id);
					const change = new ChangeEvent(
						"/value",
						props.field.value,
						newValue
					);
					props.onChange(change, true);
				})
			)
			.catch(() => {
				throw new Error("failed to create resource");
			});
	}

	function onSelectFile(event: React.ChangeEvent<HTMLInputElement>): void {
		if (!event.target.files || event.target.files.length === 0) {
			return;
		}

		const file = event.target.files[0];
		uploadFile(file);
	}

	const openFile = useCallback((id: number, name: string): void => {
		getResourceContent(id).then(data => {
			const url = URL.createObjectURL(data);
			const w = window.open(url, name);
			w?.addEventListener("beforeunload", () => URL.revokeObjectURL(url));
		});
	}, []);

	const deleteFile = useCallback(
		(id: number): void => {
			const newValue = props.field.value.filter(i => i !== id);

			const change = new ChangeEvent(
				"/value",
				props.field.value,
				newValue
			);
			props.onChange(change, true);
		},
		[props.onChange, props.field.uuid, props.field.value]
	);

	function isAllowedToEdit(): boolean {
		return (
			props.editable &&
			props.rights[props.field.uuid][InformationRight.WRITE]
		);
	}

	return (
		<Grid
			item
			container
			xs={12}
			md={6}
			columnSpacing={2}
			alignItems="center">
			<Grid item xs={12} sm={6}>
				<Typography variant="subtitle2" sx={{ wordWrap: "break-word" }}>
					{props.field.name}
					{props.field.required && " *"}
				</Typography>
			</Grid>

			<Grid item xs={12} sm={6}>
				{isAllowedToEdit() && (
					<TextField
						type="file"
						variant="standard"
						disabled={!props.editable}
						error={error !== undefined}
						helperText={error ?? ""}
						onChange={onSelectFile}
					/>
				)}

				{props.field.value.map((id, index) => (
					<Grid
						item
						container
						key={id}
						spacing={1}
						alignItems="center">
						<Grid item>
							<Link
								key={index}
								sx={{ cursor: "pointer" }}
								variant="subtitle1"
								onClick={() =>
									openFile(id, files[index] ?? id)
								}>
								<Typography
									variant="body2"
									sx={{ wordWrap: "break-word" }}>
									{files[id] ?? id}
								</Typography>
							</Link>
						</Grid>
						{props.editable && (
							<Grid item>
								<IconButton onClick={() => deleteFile(id)}>
									<DeleteOutlineIcon />
								</IconButton>
							</Grid>
						)}
					</Grid>
				))}
			</Grid>
		</Grid>
	);
}
